import { suggestedOrganizationNameFromWebsiteUrl } from "~/lib/onboarding/org-name-from-website";
import {
  decodeHtmlEntities,
  stripGenericPageTitlePrefix,
} from "~/lib/marketplace/website-location-utils";

function hostnameSlug(websiteUrl?: string): string {
  if (!websiteUrl) return "";
  try {
    return new URL(websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`)
      .hostname.replace(/^www\./i, "")
      .split(".")[0]
      ?.toLowerCase()
      .replace(/[^a-z0-9]/g, "") ?? "";
  } catch {
    return "";
  }
}

/**
 * True when a name is basically the hostname slug (e.g. "Mpbdenver" from mpbdenver.org).
 * These must NEVER override og:site_name / page title — LLMs invent expansions like
 * "Mount Paran Baptist" from MPB acronyms.
 */
export function isWeakUrlDerivedBusinessName(
  name: string | null | undefined,
  websiteUrl?: string,
): boolean {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return true;
  const slug = hostnameSlug(websiteUrl);
  const compact = trimmed.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!compact) return true;
  if (slug && (compact === slug || slug === compact)) return true;
  // Single camel/title token that equals the slug ignoring case/spaces
  if (!trimmed.includes(" ") && slug && compact.length >= 4 && slug.startsWith(compact.slice(0, 4))) {
    return compact.length <= slug.length + 2;
  }
  return false;
}

/**
 * True when a name is only a city/state (e.g. "Lone Tree, CO") — not an organization.
 * Prefer page titles like "Thrive Church" over geo-only labels.
 */
export function isLikelyLocationOnlyBusinessName(name: string | null | undefined): boolean {
  const n = (name ?? "").trim();
  if (!n) return true;
  if (
    /\b(church|parish|ministry|clinic|dental|school|academy|university|college|hospital|law|foundation|club|resort|group|llc|inc\.?|company|corp\.?)\b/i.test(
      n,
    )
  ) {
    return false;
  }
  // "Lone Tree, CO" / "Denver, Colorado"
  if (/^[A-Za-z .'-]+,\s*([A-Z]{2}|[A-Za-z]{4,})\s*$/.test(n)) return true;
  // "Lone Tree CO"
  if (/^[A-Za-z .'-]+\s+[A-Z]{2}$/.test(n) && n.split(/\s+/).length <= 4) return true;
  return false;
}

/**
 * Prefer crawled site name over a weak client hint (URL slug).
 */
export function preferCrawlBusinessName(
  crawlName: string | null | undefined,
  hint: string | null | undefined,
  websiteUrl?: string,
): string {
  const crawl = (crawlName ?? "").trim();
  const hintTrimmed = (hint ?? "").trim();
  if (
    crawl &&
    !isWeakUrlDerivedBusinessName(crawl, websiteUrl) &&
    !isLikelyServiceHoursLabel(crawl) &&
    !isLikelyLocationOnlyBusinessName(crawl)
  ) {
    if (
      !hintTrimmed ||
      isWeakUrlDerivedBusinessName(hintTrimmed, websiteUrl) ||
      isLikelyServiceHoursLabel(hintTrimmed)
    ) {
      return crawl;
    }
    // Prefer crawl when hint doesn't clearly improve on it
    const hintScore = scoreBusinessNameCandidate(hintTrimmed, websiteUrl);
    const crawlScore = scoreBusinessNameCandidate(crawl, websiteUrl);
    if (crawlScore >= hintScore) return crawl;
    return hintTrimmed;
  }
  if (
    hintTrimmed &&
    !isWeakUrlDerivedBusinessName(hintTrimmed, websiteUrl) &&
    !isLikelyServiceHoursLabel(hintTrimmed) &&
    !isLikelyLocationOnlyBusinessName(hintTrimmed)
  ) {
    return hintTrimmed;
  }
  if (crawl && !isLikelyServiceHoursLabel(crawl) && !isLikelyLocationOnlyBusinessName(crawl)) {
    return crawl;
  }
  return websiteUrl ? suggestedOrganizationNameFromWebsiteUrl(websiteUrl) : "";
}

/** Long descriptive SEO lines (e.g. "A Catholic, Jesuit School for Boys in New York City"). */
export function isLikelySeoTagline(name: string): boolean {
  const n = decodeHtmlEntities(name.trim());
  if (!n) return false;
  // Legitimate org names that start with The/A/An (e.g. "The Country Club at Castle Pines").
  if (
    /^(The|A|An)\s+.+\b(Country Club|Golf Club|Yacht Club|Club|Resort|School|Academy|University|College|Hospital|Clinic|Church|Parish|Foundation|Center|Centre|Inn|Hotel|Spa|Group|Associates|Partners)\b/i.test(
      n,
    )
  ) {
    return false;
  }
  if (n.length > 48) return true;
  if (/\b(for boys|for girls|for men|for women|for families)\b/i.test(n)) return true;
  if (/\b(located in|serving the|committed to|dedicated to|providing|premier|leading)\b/i.test(n)) {
    return true;
  }
  if (/\bin (New York|Los Angeles|Chicago|[A-Z][a-z]+ (City|County|Area))\b/.test(n)) return true;
  if (n.split(/\s+/).length > 8) return true;
  if (/^(A|An|The)\s+[A-Z]/.test(n) && n.split(/\s+/).length >= 6) return true;
  return false;
}

/**
 * True when a "name" is actually service times / schedule copy
 * (e.g. "Sundays 9am & 11am") — never use as organization name.
 */
export function isLikelyServiceHoursLabel(name: string | null | undefined): boolean {
  const n = decodeHtmlEntities((name ?? "").trim());
  if (!n) return false;
  if (/\b(church|parish|ministry|clinic|dental|school|academy|university|college)\b/i.test(n)) {
    return false;
  }
  if (
    /\b(sunday|monday|tuesday|wednesday|thursday|friday|saturday|sundays|weekdays)\b/i.test(n) &&
    /\b(\d{1,2}(:\d{2})?\s*(am|pm)|noon|midnight)\b/i.test(n)
  ) {
    return true;
  }
  if (/^\s*\d{1,2}(:\d{2})?\s*(am|pm)\b/i.test(n) && /(&|and|,|–|-)/i.test(n)) {
    return true;
  }
  return false;
}

function scoreBusinessNameCandidate(name: string, websiteUrl?: string): number {
  const n = decodeHtmlEntities(name.trim());
  if (!n || n.length < 2) return -100;

  let score = 0;
  const words = n.split(/\s+/);

  if (isLikelyServiceHoursLabel(n)) score -= 50;
  if (isLikelySeoTagline(n)) score -= 25;
  if (isLikelyLocationOnlyBusinessName(n)) score -= 30;
  if (words.length >= 1 && words.length <= 5) score += 4;
  if (n.length >= 3 && n.length <= 36) score += 4;
  if (n.length > 50) score -= 8;
  if (
    /\b(High School|Academy|University|College|Hospital|Clinic|Dental|Law Firm|Church|Foundation|Country Club|Golf Club|Yacht Club|Club|Resort|Group|LLC|Inc\.?)\b/i.test(
      n,
    )
  ) {
    score += 10;
  }

  const slug = hostnameSlug(websiteUrl);
  if (slug) {
    const compact = n.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (compact === slug) {
      // Bare URL slug (e.g. "Mpbdenver") must lose to og:site_name / page title.
      score -= 20;
    } else if (
      compact.includes(slug) ||
      slug.includes(compact.slice(0, Math.min(5, compact.length)))
    ) {
      score += 12;
    }
  }

  return score;
}

/** Pick the best title or metadata segment (brand name vs trailing SEO tagline). */
export function pickBestBusinessNameCandidate(
  candidates: string[],
  websiteUrl?: string,
): string | null {
  const seen = new Set<string>();
  const ranked = candidates
    .map((raw) => decodeHtmlEntities(raw.trim()))
    .filter((name) => {
      const key = name.toLowerCase();
      if (!name || name.length < 2 || name.length > 80 || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((name) => ({ name, score: scoreBusinessNameCandidate(name, websiteUrl) }))
    .sort((a, b) => b.score - a.score);

  const best = ranked[0];
  if (!best || best.score < 0) return null;
  return best.name;
}

/** Short display name for hire onboarding (strip SEO taglines after " - "). */
export function normalizeHireCompanyDisplayName(
  raw: string | null | undefined,
  websiteUrl?: string,
): string {
  const trimmed = decodeHtmlEntities((raw ?? "").trim());
  if (!trimmed) return "";

  const withoutPagePrefix = stripGenericPageTitlePrefix(trimmed);
  if (withoutPagePrefix !== trimmed && withoutPagePrefix.length >= 2) {
    const cleaned =
      withoutPagePrefix.length <= 80
        ? withoutPagePrefix
        : withoutPagePrefix.slice(0, 80).trim();
    if (!isLikelySeoTagline(cleaned)) return cleaned;
  }

  const parts = trimmed.split(/\s[-–—|]\s/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const best = pickBestBusinessNameCandidate(parts, websiteUrl);
    if (best) return best;
  }

  if (isLikelySeoTagline(trimmed) && websiteUrl) {
    return suggestedOrganizationNameFromWebsiteUrl(websiteUrl);
  }

  const segment = parts[0]?.trim() ?? trimmed;
  if (segment && segment.length >= 2 && segment.length <= 80 && !isLikelySeoTagline(segment)) {
    return segment;
  }

  if (trimmed.length <= 80 && !isLikelySeoTagline(trimmed)) return trimmed;
  return websiteUrl ? suggestedOrganizationNameFromWebsiteUrl(websiteUrl) : trimmed.slice(0, 80).trim();
}

/** Prefer readable spacing from URL when crawl returns a domain slug (e.g. arapahoefamilydentistry). */
export function resolveHireBusinessDisplayName(
  raw: string | null | undefined,
  websiteUrl?: string,
): string {
  const trimmed = (raw ?? "").trim();
  if (!trimmed && websiteUrl) {
    return suggestedOrganizationNameFromWebsiteUrl(websiteUrl);
  }

  const normalized = normalizeHireCompanyDisplayName(trimmed, websiteUrl);
  if (isLikelySeoTagline(normalized) && websiteUrl) {
    return suggestedOrganizationNameFromWebsiteUrl(websiteUrl);
  }

  const looksLikeDomainSlug =
    !normalized.includes(" ") &&
    websiteUrl &&
    (normalized.length > 10 || /^[a-z0-9]+$/i.test(normalized));

  if (looksLikeDomainSlug) {
    const fromUrl = suggestedOrganizationNameFromWebsiteUrl(websiteUrl);
    if (fromUrl.includes(" ") || fromUrl.toLowerCase() !== normalized.toLowerCase()) {
      return fromUrl;
    }
  }

  if (!normalized.includes(" ") && websiteUrl) {
    const fromUrl = suggestedOrganizationNameFromWebsiteUrl(websiteUrl);
    if (fromUrl.includes(" ")) return fromUrl;
  }

  if (/^[A-Z][a-z]+[A-Z]/.test(normalized) && !normalized.includes(" ")) {
    return normalized.replace(/([a-z])([A-Z])/g, "$1 $2");
  }

  return normalized || (websiteUrl ? suggestedOrganizationNameFromWebsiteUrl(websiteUrl) : trimmed);
}
